/**
 * Nomination validation utilities
 * Requirements: 2.3 - Validate required fields and reject empty/whitespace-only values
 */

import {
  CreateNominationInput,
  Nomination,
  AwardType,
  NominationKind,
  NominationStatus,
  TeamMember,
} from './nomination';

/**
 * The id stored in `nomineeId` for a team.
 *
 * A team has no email, and nomineeId is required by the schema, the serializer and every screen —
 * so it needs a value that is stable for the same team name and can never collide with a person's
 * address. The `team:` prefix guarantees the second part: an email cannot contain a colon before
 * its local part, so no employee can ever produce this id.
 */
export function teamNomineeId(teamName: string): string {
  const slug = String(teamName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `team:${slug || 'unnamed'}`;
}

/** Whether a nomination describes a team. Absent means Individual — see NominationKind. */
export const isTeamNomination = (
  value: { nominationType?: NominationKind } | null | undefined
): boolean => value?.nominationType === NominationKind.TEAM;

/**
 * The members of a team, deduplicated by id.
 *
 * Applied on the way in as well as enforced in the picker: the picker is one entry point, and a
 * draft reloaded from storage or an admin edit is another. A team listing the same person twice
 * would show them twice in the detail view and count them twice in "4 members".
 */
export function uniqueTeamMembers(members: TeamMember[] | undefined): TeamMember[] {
  const seen = new Set<string>();
  return (members || []).filter(member => {
    const key = String(member?.id || '').trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Validation result interface (reuses the same pattern as validation.ts)
 */
export interface NominationValidationResult {
  isValid: boolean;
  errors: string[];
}

const AWARD_TYPES = new Set(Object.values(AwardType));
const NOMINATION_STATUSES = new Set(Object.values(NominationStatus));

/**
 * Checks if a string value is empty or whitespace-only.
 */
function isBlank(value: unknown): boolean {
  return typeof value !== 'string' || value.trim().length === 0;
}

/**
 * Validates a CreateNominationInput.
 * Rejects when any required field (nomineeId, awardType, reason) is empty or whitespace-only.
 * Requirements: 2.3
 */
export function validateCreateNominationInput(
  input: Partial<CreateNominationInput>
): NominationValidationResult {
  const errors: string[] = [];

  if (isBlank(input.nomineeId)) {
    errors.push('Nominee/Team ID is required');
  }

  if (!input.awardType || !AWARD_TYPES.has(input.awardType)) {
    errors.push('Valid award type is required');
  }

  /*
   * Team rules run only for a team, so nothing here can make an individual nomination — the only
   * kind that existed before this — newly invalid. An absent nominationType is an individual.
   */
  if (isTeamNomination(input)) {
    if (isBlank(input.teamName)) {
      errors.push('Team name is required');
    }
    if (uniqueTeamMembers(input.teamMembers).length === 0) {
      errors.push('A team nomination needs at least one team member');
    }
  }

  if (isBlank(input.reason)) {
    errors.push('Nomination reason is required');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates a full Nomination object for structural integrity.
 * Requirements: 2.3
 */
export function validateNomination(
  nomination: Partial<Nomination>
): NominationValidationResult {
  const errors: string[] = [];

  if (isBlank(nomination.id)) {
    errors.push('Nomination ID is required');
  }

  if (isBlank(nomination.nomineeId)) {
    errors.push('Nominee/Team ID is required');
  }

  if (isBlank(nomination.nomineeName)) {
    errors.push('Nominee/Team name is required');
  }

  if (isBlank(nomination.nominatorId)) {
    errors.push('Nominator ID is required');
  }

  if (isBlank(nomination.nominatorName)) {
    errors.push('Nominator name is required');
  }

  if (!nomination.awardType || !AWARD_TYPES.has(nomination.awardType)) {
    errors.push('Valid award type is required');
  }

  if (isBlank(nomination.reason)) {
    errors.push('Nomination reason is required');
  }

  if (!nomination.status || !NOMINATION_STATUSES.has(nomination.status)) {
    errors.push('Valid nomination status is required');
  }

  if (isBlank(nomination.createdAt)) {
    errors.push('Created timestamp is required');
  }

  if (isBlank(nomination.updatedAt)) {
    errors.push('Updated timestamp is required');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
